// @greenlight-spec: 11-observability.md
// @greenlight-spec: 16-api-conventions.md
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { PaginationQuerySchema, paginated, AuditStatusSchema, AttributionCoverageSchema, } from '@greenlight/shared';
import { errorResponses } from './common.js';
const c = initContract();
// The audit event row the /audit table and admin queries read. The authoritative
// storage shape lives in 15-database-schema.md (audit_log); the read handler
// projects those columns onto this row (docs/11 §Query surface). Storage→contract
// mapping the handler owns: `action` ← event_type; `actorUserId` ← actor_id for
// user/agent actors (null for system); `actorAgentSessionId` ← payload.actor.agent_id
// for agent actors; `resourceType`/`resourceId` ← payload.target.kind/id; `outcome`
// folds the stored succeeded|failed|denied to success|failure; `details` ← payload.metadata.
// `resourceType` and `actorAgentSessionId` are nullable because not every governed
// action has a target object or an agent behind it, and the agent identifier the write
// path records is not a UUID.
//
// `actorRole`/`actorChannel` ← the audit_log columns of the same name, present for
// every row once the write path sets them. `method`/`path`/`responseStatus`/
// `durationMs`/`credentialUsed` ← payload.metadata, present only on proxy/data-access
// rows (docs/11 §Log format) — omitted (not `null`) on every other row, so the drawer
// can gate an entire "proxy request" section on one field's presence rather than
// rendering an empty section. `responseSizeBytes` is declared but not yet populated
// by any write path (T-3352) — deferred until the proxy captures it, same as source
// IP/geo (docs/11 §Query surface).
export const AuditEventSchema = z.object({
    id: z.string().min(1),
    orgId: z.string().uuid(),
    appId: z.string().uuid().nullable(),
    actorUserId: z.string().uuid().nullable(),
    actorAgentSessionId: z.string().min(1).nullable(),
    action: z.string().min(1),
    resourceType: z.string().min(1).nullable(),
    resourceId: z.string().min(1).nullable(),
    outcome: z.enum(['success', 'failure']),
    requestId: z.string().min(1),
    createdAt: z.string().datetime({ offset: true }),
    details: z.unknown().optional(),
    actorRole: z.string().min(1).optional(),
    actorChannel: z.string().min(1).optional(),
    method: z.string().min(1).optional(),
    path: z.string().min(1).optional(),
    responseStatus: z.number().int().optional(),
    responseSizeBytes: z.number().int().optional(),
    durationMs: z.number().int().optional(),
    credentialUsed: z.string().min(1).optional(),
});
// The window is bounded by audit retention (docs/11 §Storage): asking for more
// days than `audit_log` keeps would return a coverage figure quietly computed
// over a shorter period than the caller requested.
export const AttributionQuerySchema = z.object({
    window_days: z.coerce.number().int().min(1).max(30).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
export const AuditQuerySchema = PaginationQuerySchema.extend({
    appId: z.string().uuid().optional(),
    actorUserId: z.string().uuid().optional(),
    action: z.string().optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
});
export const auditContract = c.router({
    list: {
        method: 'GET',
        path: '/api/audit',
        query: AuditQuerySchema,
        responses: {
            200: paginated(AuditEventSchema),
            ...errorResponses,
        },
        summary: 'Query the audit log newest-first (cursor-paginated by created_at desc), filtered by app, actor, action, and time range. IT-scoped.',
    },
    status: {
        method: 'GET',
        path: '/api/audit/status',
        responses: {
            200: AuditStatusSchema,
            ...errorResponses,
        },
        summary: 'Return audit health: last write time, retention config, WORM status, and compliance readiness.',
    },
    attribution: {
        method: 'GET',
        path: '/api/audit/attribution',
        query: AttributionQuerySchema,
        responses: {
            200: AttributionCoverageSchema,
            ...errorResponses,
        },
        summary: "Per-app proxy attribution coverage over a recent window: how many of each app's proxy calls name a user, and whether an app with none served signed-in users anyway. IT-scoped.",
    },
});
//# sourceMappingURL=audit.js.map